import { useState, useEffect, useCallback } from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import dynamic from 'next/dynamic';
import { Portal } from 'react-portal';
import { Transition } from '@headlessui/react';
import useScrollBlock from '@/lib/useScrollBlock';
import Tooltip from '@/components/Tooltip';
import LoadingIndicator from '@/components/LoadingIndicator';

const PaymentezPaymentWorkflow = dynamic(
  () => import('./PaymentezPaymentWorkflow'),
  {
    ssr: false,
    loading: () => <PaymentezPaymentWorkflowLoadingIndicator />,
  }
);

function PaymentezPaymentWorkflowLoadingIndicator() {
  return (
    <div className="absolute z-20 inset-0 flex items-center justify-center bg-warm-50">
      <div className="flex space-x-4 items-center relative z-10">
        <LoadingIndicator />
        <div className="font-normal text-gray-500">
          Cargando pasarela de pago Paymentez
        </div>
      </div>
    </div>
  );
}

const htmlID = 'PaymentezPaymentWorkflowDialog';

function removePaymentWorkflowDialog() {
  const target = document.getElementById(htmlID);
  if (target) {
    unmountComponentAtNode(target);
    target.parentNode.removeChild(target);
  }
}

function PaymentWorkflowView({
  checkout,
  onPaymentAccepted,
  onPaymentRejected,
  onPaymentCanceled,
}) {
  const onPaymentAcceptedCallback = useCallback(
    (d) => {
      removePaymentWorkflowDialog();
      onPaymentAccepted(d);
    },
    [onPaymentAccepted]
  );

  const onPaymentCanceledCallback = useCallback(() => {
    removePaymentWorkflowDialog();
    onPaymentCanceled();
  }, [onPaymentCanceled]);

  const onCancelPaymentClickCallback = useCallback(() => {
    onPaymentCanceledCallback();
  }, [onPaymentCanceledCallback]);

  const [isShowing, setShowing] = useState(false);

  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    blockScroll();
    setShowing(true);
    return () => allowScroll();
  }, [blockScroll, allowScroll]);

  return (
    <Portal>
      <Transition show={isShowing}>
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Transition.Child
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            as="div"
            className="absolute inset-0"
          >
            <div className="bg-gray-900 bg-opacity-90 absolute inset-0 pointer-events-none z-10" />
          </Transition.Child>
          <div className="absolute inset-0 z-50 flex items-center justify-center">
            <PaymentezPaymentWorkflow
              checkout={checkout}
              onPaymentAccepted={onPaymentAcceptedCallback}
              onPaymentRejected={onPaymentRejected}
              onPaymentCanceled={onPaymentCanceledCallback}
            />
          </div>
          <div className="absolute top-0 right-0 z-50 p-4">
            <Tooltip tooltip="Cancelar pago con PayPal">
              <button
                className="text-gray-400 hover:text-white w-12 h-12 flex items-center justify-center focus:ring-4 rounded-full border border-transparent focus:border-gray-900 focus:ring-gray-200 focus:ring-opacity-5 focus:text-white"
                onClick={onCancelPaymentClickCallback}
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </Tooltip>
          </div>
        </div>
      </Transition>
    </Portal>
  );
}

export default function runPaymentezPaymentWorkflow({
  checkout,
  onPaymentAccepted = () => console.log('onPaymentAccepted() not implemented'),
  onPaymentRejected = () => console.log('onPaymentRejected() not implemented'),
  onPaymentCanceled = () => console.log('onPaymentCanceled() not implemented'),
}) {
  let divTarget = document.getElementById(htmlID);
  if (!divTarget) {
    document.body.children[0].classList.add(htmlID);
    divTarget = document.createElement('div');
    divTarget.id = htmlID;
    document.body.appendChild(divTarget);
  }

  render(
    <PaymentWorkflowView
      checkout={checkout}
      onPaymentAccepted={onPaymentAccepted}
      onPaymentRejected={onPaymentRejected}
      onPaymentCanceled={onPaymentCanceled}
    />,
    divTarget
  );
}
